/**
 * Capped localStorage for meditation session history.
 *
 * Designed so swapping localStorage for a server-side `fetch('/api/sessions')`
 * is a single-file change — the Elm-facing port contract stays identical.
 */

const SESSIONS_KEY = "mc.sessions.v1" as const;
const SESSIONS_CAP = 30 as const;

type SessionRecord = {
  readonly meditationId: string;
  readonly meditationName: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly durationSeconds: number;
  readonly completed: boolean;
};

type Ports = {
  readonly persistSession?: { readonly subscribe: (h: (s: unknown) => void) => void } | undefined;
  readonly loadSessions?: { readonly subscribe: (h: (_: null) => void) => void } | undefined;
  readonly sessionsLoaded?: { readonly send: (s: unknown) => void } | undefined;
};

const isSessionRecord = (v: unknown): v is SessionRecord => {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r["meditationId"] === "string" &&
    typeof r["meditationName"] === "string" &&
    typeof r["startTime"] === "string" &&
    typeof r["endTime"] === "string" &&
    typeof r["durationSeconds"] === "number" &&
    typeof r["completed"] === "boolean"
  );
};

const readAll = (): readonly SessionRecord[] => {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSessionRecord);
  } catch {
    return [];
  }
};

const writeAll = (sessions: readonly SessionRecord[]): void => {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  } catch {
    /* quota exceeded — silently drop (capped already, very unlikely) */
  }
};

export const wireStorageBridge = (ports: Ports): void => {
  ports.persistSession?.subscribe((incoming) => {
    if (!isSessionRecord(incoming)) {
      console.warn("[storage-bridge] rejected malformed session", incoming);
      return;
    }
    const next = [incoming, ...readAll()].slice(0, SESSIONS_CAP);
    writeAll(next);
  });

  ports.loadSessions?.subscribe(() => {
    ports.sessionsLoaded?.send(readAll());
  });
};
